import { Typography } from 'opensolar-ui'
import { ChipSelection } from 'projectSections/sections/design/sideDrawer/autoDesign/V2/components/ChipSelection'
import { ThumbsButtons } from 'projectSections/sections/design/sideDrawer/autoDesign/V2/components/ThumbsButtons'
import React from 'react'
import { useTranslate } from 'react-admin'
import { useChatMessageFeedback } from './ChatMessageFeedbackProvider'
import { useChatScroll } from './ChatScrollContext'

interface ChatMessageFeedbackButtonsProps {
  renderButtons?: boolean
  renderExpandableContent?: boolean
}

const CUSTOM_CHIP_KEY = 'Something Else'

export const ChatMessageFeedbackButtons: React.FC<ChatMessageFeedbackButtonsProps> = ({
  renderButtons = false,
  renderExpandableContent = false,
}) => {
  const translate = useTranslate()
  const { scrollToBottom } = useChatScroll()
  const {
    state,
    actions,
    isSubmitting,
    setCustomFeedbackText,
    isFeedbackSubmitted,
    getFeedbackOptions,
    getQuestionText,
    handleThumbsClick,
    handleChipSubmit,
    handleCancel,
  } = useChatMessageFeedback()

  // Render buttons (thumbs up/down)
  if (renderButtons) {
    // If feedback is submitted, show nothing (buttons are hidden)
    if (isFeedbackSubmitted) {
      return null
    }

    // Show thumbs buttons
    return (
      <ThumbsButtons
        isPositive={state.isPositive}
        onThumbsClick={handleThumbsClick}
        thumbsUpTooltipKey="This response was helpful!"
        thumbsDownTooltipKey="This response could be improved"
        size="small"
      />
    )
  }

  // Render expandable content (chips, forms, "Feedback submitted" message)
  if (renderExpandableContent) {
    if (isFeedbackSubmitted) {
      return (
        <Typography variant="body2" color="textSecondary" style={{ fontSize: '12px', marginTop: '8px' }}>
          {translate('Feedback submitted')}
        </Typography>
      )
    }

    // Show chip selection if active
    if (state.showChipSelection) {
      return (
        <ChipSelection
          selectedChips={state.selectedChips}
          onChipToggle={actions.handleChipToggle}
          onSubmit={handleChipSubmit}
          onCancel={handleCancel}
          isSubmitting={isSubmitting}
          feedbackOptions={getFeedbackOptions().map((option) => option.key)}
          questionText={translate(getQuestionText())}
          customFeedback={{
            enabled: true,
            text: state.customFeedbackText,
            placeholder: 'Tell us more about what could be improved...',
            rows: 3,
            customChipKey: CUSTOM_CHIP_KEY,
            onChange: setCustomFeedbackText,
          }}
          onCustomFieldShown={() => {
            setTimeout(() => scrollToBottom(), 100)
          }}
        />
      )
    }

    return null
  }

  return null
}
